import { ContractPriceImportRun } from '../../lib/api';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';

const parts = (n: number) => `${n} ${n === 1 ? 'part' : 'parts'}`;

interface ImportRunProgressProps {
  run: ContractPriceImportRun | null;
  /** The run has waited for a worker longer than expected. */
  stalled: boolean;
}

/**
 * CR-8663: the import modal while a Price Book import is being saved. The
 * work runs on the server, so the window can be closed without stopping it.
 */
export function ImportRunProgress({ run, stalled }: ImportRunProgressProps) {
  const queued = !run || run.run_status === 'queued';
  return (
    <div className="space-y-3 py-10 text-center" data-testid="contract-price-import-progress">
      <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
      <h3 className="text-lg font-semibold">
        {queued ? 'Waiting for the import to start…' : 'Saving contracts and parts…'}
      </h3>
      {run && (
        <p className="text-sm text-muted-foreground">
          {run.row_count} {run.row_count === 1 ? 'row' : 'rows'}
        </p>
      )}
      {queued && stalled && (
        <p className="text-sm text-amber-700 max-w-md mx-auto" data-testid="contract-price-import-stalled">
          It will finish in the background, so you can close this window.
        </p>
      )}
    </div>
  );
}

/**
 * CR-8663: the PPV line under the import counts. PPV is recalculated after
 * the contracts and parts are saved, so the counts show first and this line
 * follows it to the end.
 */
export function PpvStatusLine({ run }: { run: ContractPriceImportRun }) {
  const total = run.ppv_parts_total;
  if (run.run_status === 'recalculating_ppv') {
    return (
      <div className="space-y-1" data-testid="contract-price-import-ppv">
        <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Updating PPV: {run.ppv_parts_done} of {parts(total)}…
        </p>
        <p className="text-xs text-muted-foreground">
          You can close this window — PPV finishes in the background.
        </p>
      </div>
    );
  }
  if (run.run_status !== 'completed' || total === 0) return null;
  if (run.ppv_parts_failed > 0) {
    return (
      <p className="flex items-center justify-center gap-2 text-sm text-amber-700" data-testid="contract-price-import-ppv">
        <AlertCircle className="h-4 w-4" />
        PPV could not be updated for {run.ppv_parts_failed} of {parts(total)}.
      </p>
    );
  }
  return (
    <p className="flex items-center justify-center gap-2 text-sm text-green-700" data-testid="contract-price-import-ppv">
      <CheckCircle className="h-4 w-4" />
      PPV updated for {parts(total)}.
    </p>
  );
}

/**
 * CR-8663: the run's state could not be read. The import itself may well
 * still be running, so this never says it failed.
 */
export function ImportRunContactLost() {
  return (
    <p className="flex items-center justify-center gap-2 text-sm text-amber-700" data-testid="contract-price-import-contact-lost">
      <AlertCircle className="h-4 w-4" />
      Can't check the import's progress right now. It may still be running — check the Pricebooks list shortly.
    </p>
  );
}
